import * as React from 'react'

const IMAGE_BASE = `${window.postbuildEnv.CDN_URL}/images/evidence`

export const VISIT = 'visit'
export const PERCH = 'perch'

export const GREAT_EVIDENCE = 'Great Evidence!'
export const STRONG_SENTENCE = 'That’s a strong sentence!'

const BUBBLE_TEXT = {
  [VISIT]: GREAT_EVIDENCE,
  [PERCH]: STRONG_SENTENCE,
}

// Fractions of the 180x180 artboard the standing frame's artwork covers, measured from the SVGs.
const STANDING_INK_HEIGHT = 0.568
export const STANDING_INK_RIGHT = 0.747
export const STANDING_INK_BOTTOM = 0.922
// The wings-down frame's tail reaches further right than the standing frame.
export const FLAPPING_INK_RIGHT = 0.875
// The bird's height on screen, not its artboard's.
const QUAIL_HEIGHT = 87
// Phones have less room above the response box.
const QUAIL_HEIGHT_NARROW = 61
const NARROW_VIEWPORT = 600
// The feet sit just below the box's top border and the flapping tail just past its right edge.
// PERCH_OVERHANG must match overflow-clip-margin in celebration.scss.
export const PERCH_OVERHANG = 2
export const PERCH_GRIP = 2

// Spec timings from submission. Durations must match celebration.scss.
const ENTRY_DELAY = 150
const FLIGHT_DURATION = 1000
const BUBBLE_DELAY = 1550
const EXIT_DELAY = 3600
const EXIT_DURATION = 1300

// Offsets from the landing point where the quail starts and, for a visit, leaves.
const START_ABOVE = 260
const EXIT_ABOVE = 320
const EXIT_LEFT = 420

const FRAMES = [
  'quail-standing',
  'quail-smiling',
  'quail-wings-up',
  'quail-wings-down',
]

interface QuailCelebrationProps {
  mode: typeof VISIT | typeof PERCH;
  onDone?: () => void;
}

const prefersReducedMotion = () => !!window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches

const quailSize = () => (window.innerWidth <= NARROW_VIEWPORT ? QUAIL_HEIGHT_NARROW : QUAIL_HEIGHT) / STANDING_INK_HEIGHT

// Positioned inside the response box's wrapper, so she moves with the box as the page scrolls or reflows.
const QuailCelebration = ({ mode, onDone, }: QuailCelebrationProps) => {
  const [size, setSize] = React.useState(quailSize)
  const [launched, setLaunched] = React.useState(false)
  const [landed, setLanded] = React.useState(false)
  const [bubbleVisible, setBubbleVisible] = React.useState(false)
  const [exiting, setExiting] = React.useState(false)

  React.useEffect(() => {
    const handleResize = () => setSize(quailSize())
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  React.useEffect(() => {
    const timers = []
    const at = (delay, callback) => timers.push(window.setTimeout(callback, delay))

    if (prefersReducedMotion()) {
      setLaunched(true)
      setLanded(true)
    } else {
      at(ENTRY_DELAY, () => setLaunched(true))
      at(ENTRY_DELAY + FLIGHT_DURATION, () => setLanded(true))
    }

    at(BUBBLE_DELAY, () => setBubbleVisible(true))

    if (mode === VISIT) {
      at(EXIT_DELAY, () => { setBubbleVisible(false); setExiting(true) })
      at(EXIT_DELAY + EXIT_DURATION, () => onDone && onDone())
    }

    return () => timers.forEach(window.clearTimeout)
  }, [mode])

  const offsetY = exiting ? -EXIT_ABOVE : (launched ? 0 : -START_ABOVE)
  const offsetX = exiting ? -EXIT_LEFT : 0
  const perched = landed && !exiting
  const top = PERCH_GRIP - (size * STANDING_INK_BOTTOM)
  const right = -((size * (1 - FLAPPING_INK_RIGHT)) + PERCH_OVERHANG)

  return (
    <div aria-hidden="true" className={`quail-celebration ${perched ? 'perched' : ''} ${bubbleVisible ? 'speaking' : ''} ${exiting ? 'exiting' : ''}`}>
      <div
        className="quail-flight-x"
        style={{ right, top, transform: `translateX(${offsetX}px)` }}
      >
        <div className="quail-flight-y" style={{ transform: `translateY(${offsetY}px)` }}>
          <div className="quail-anchor" style={{ width: size, height: size }}>
            {FRAMES.map((name) => (
              <img alt="" className={`quail-frame ${name}`} key={name} src={`${IMAGE_BASE}/${name}.svg`} />
            ))}
            {bubbleVisible && <p className="quail-bubble">{BUBBLE_TEXT[mode]}</p>}
          </div>
        </div>
      </div>
    </div>
  )
}

export default QuailCelebration
